import React from "react";
import { useGetCurrentUserApi } from "../api/methods/use-get-current-user-api";

const STORAGE_KEY = "supervisorModeEnabled";

const stringToBoolean = (value: string | null): boolean | null => {
  if (value === "false") return false;
  if (value === "true") return true;
  return null;
};

const SupervisorModeProviderContext = React.createContext({
  supervisorMode: false,
  setSupervisorMode: (_data: boolean) => {},
});

export const SupervisorModeProvider: React.FC<{ children: React.ReactNode }> = (
  props
) => {
  const { data: currentUser } = useGetCurrentUserApi();

  const [supervisorMode, setSupervisorModeInternal] = React.useState(() => {
    // Try to get saved value from localStorage first
    const savedValue = window.localStorage.getItem(STORAGE_KEY);
    const savedBoolean = stringToBoolean(savedValue);

    // If there's a saved value, use it
    if (savedBoolean !== null) {
      return savedBoolean;
    }

    // Otherwise, default to false until we load the user
    return false;
  });

  // Update default based on user permissions when user loads
  React.useEffect(() => {
    if (currentUser) {
      const savedValue = window.localStorage.getItem(STORAGE_KEY);
      const savedBoolean = stringToBoolean(savedValue);

      // Only update if there's no saved preference
      if (savedBoolean === null) {
        const userDefault =
          currentUser.attributes.admin ||
          currentUser.attributes.qc_supervisor ||
          currentUser.attributes.tagger_is_qcer ||
          false;
        setSupervisorModeInternal(userDefault);
      }
    }
  }, [currentUser]);

  const setSupervisorMode = React.useCallback((value: boolean) => {
    window.localStorage.setItem(STORAGE_KEY, String(value));
    setSupervisorModeInternal(value);
  }, []);

  return (
    <SupervisorModeProviderContext.Provider
      value={{ supervisorMode, setSupervisorMode }}
    >
      {props.children}
    </SupervisorModeProviderContext.Provider>
  );
};

export const useSupervisorMode = (): boolean => {
  const { supervisorMode } = React.useContext(SupervisorModeProviderContext);

  return supervisorMode;
};

export const useSetSupervisorMode = (): ((data: boolean) => void) => {
  const { setSupervisorMode } = React.useContext(SupervisorModeProviderContext);

  return setSupervisorMode;
};
